import React, { useState, useRef } from "react";
import {
  CheckCircle,
  Upload,
  X,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Loader2,
  FileText,
} from "lucide-react";

interface FileUpload {
  file: File;
  preview: string;
}

interface FormData {
  // Personal Information
  firstName: string;
  lastName: string;
  fullNameNativeLanguage: string;
  sex: string;
  maritalStatus: string;
  dateOfBirth: string;
  placeOfBirth: string;
  nationality: string;
  hasOtherNationalities: string;
  nationalIdNumber: string;
  hasUSSSN: string;
  usSSNNumber: string;
  homeAddress: string;
  studentCity: string;
  studentCountry: string;
  sameMailingAddress: string;
  primaryPhone: string;
  mobilePhone: string;
  email: string;
  socialMediaLinks: string;

  // Current Location
  currentLocationCity: string;
  currentLocationCountry: string;

  // Passport Information
  passportNumber: string;
  passportIssuingAuthority: string;
  passportCountryIssued: string;
  passportCityIssued: string;
  passportIssueDate: string;
  passportExpiryDate: string;
  hasLostPassport: string;

  // Travel Information
  purposeOfTrip: string;
  programName: string;
  intendedArrivalDate: string;
  travelEndDate: string;
  intendedLengthOfStay: string;
  usStayAddress: string;
  tripPayingEntity: string;
  tripPayerName: string;
  tripPayerPhone: string;
  tripPayerEmail: string;
  tripPayerRelationship: string;
  tripPayerAddressSameAsHome: string;
  tripPayerAddress: string;
  hasTravelCompanions: string;
  travelCompanionRelationship: string;

  // Previous U.S. Travel & Visa
  hasBeenToUS: string;
  hasUSVisa: string;
  usVisaNumber: string;
  hasBeenRefusedVisa: string;

  // U.S. Contact Information
  usContactName: string;
  usContactOrganization: string;
  usContactRelationship: string;
  usContactAddress: string;
  usContactPhone: string;
  usContactEmail: string;

  // Parent/Guardian Information
  parentName: string;
  parentEmail: string;
  parentPhone: string;
  parentRelationship: string;

  // Emergency Contact
  emergencyContactName: string;
  emergencyContactPhone: string;
  emergencyContactRelationship: string;

  // Family Information
  fatherSurname: string;
  fatherGivenNames: string;
  fatherDOB: string;
  fatherInUS: string;
  motherSurname: string;
  motherGivenNames: string;
  motherDOB: string;
  motherInUS: string;
  hasRelativesInUS: string;
  relativeInfo: string;
  spouseInfo: string;

  // Work/Education Information
  primaryOccupation: string;
  employerSchoolName: string;
  employerSchoolAddress: string;
  educationStartDate: string;
  monthlySalary: string;
  jobDuties: string;
  languagesSpoken: string;
  countriesTraveledLast5Years: string;

  // Security Questions
  hasCommunicableDisease: string;
  hasMentalDisorder: string;
  isDrugAbuser: string;
  hasBeenArrested: string;
  hasViolatedDrugLaw: string;
  hasEngagedInProstitution: string;
  hasEngagedInMoneyLaundering: string;
  hasEngagedInEspionage: string;
  hasEngagedInTerrorism: string;
  hasProvidedTerroristSupport: string;
  isTerroristMember: string;
  hasEngagedInGenocide: string;
  hasEngagedInTorture: string;
  hasEngagedInViolence: string;
  hasViolatedReligiousFreedom: string;
  hasCommittedVisaFraud: string;
  hasWithheldCustody: string;
  hasVotedIllegally: string;
  hasRenouncedCitizenshipForTax: string;

  // Consent
  consentAcknowledgment: boolean;
}

const initialFormData: FormData = {
  // Personal Information
  firstName: "",
  lastName: "",
  fullNameNativeLanguage: "",
  sex: "",
  maritalStatus: "",
  dateOfBirth: "",
  placeOfBirth: "",
  nationality: "",
  hasOtherNationalities: "",
  nationalIdNumber: "",
  hasUSSSN: "",
  usSSNNumber: "",
  homeAddress: "",
  studentCity: "",
  studentCountry: "",
  sameMailingAddress: "",
  primaryPhone: "",
  mobilePhone: "",
  email: "",
  socialMediaLinks: "",

  // Current Location
  currentLocationCity: "",
  currentLocationCountry: "",

  // Passport Information
  passportNumber: "",
  passportIssuingAuthority: "",
  passportCountryIssued: "",
  passportCityIssued: "",
  passportIssueDate: "",
  passportExpiryDate: "",
  hasLostPassport: "",

  // Travel Information
  purposeOfTrip: "",
  programName: "",
  intendedArrivalDate: "",
  travelEndDate: "",
  intendedLengthOfStay: "",
  usStayAddress: "",
  tripPayingEntity: "",
  tripPayerName: "",
  tripPayerPhone: "",
  tripPayerEmail: "",
  tripPayerRelationship: "",
  tripPayerAddressSameAsHome: "",
  tripPayerAddress: "",
  hasTravelCompanions: "",
  travelCompanionRelationship: "",

  // Previous U.S. Travel & Visa
  hasBeenToUS: "",
  hasUSVisa: "",
  usVisaNumber: "",
  hasBeenRefusedVisa: "",

  // U.S. Contact Information
  usContactName: "",
  usContactOrganization: "",
  usContactRelationship: "",
  usContactAddress: "",
  usContactPhone: "",
  usContactEmail: "",

  // Parent/Guardian Information
  parentName: "",
  parentEmail: "",
  parentPhone: "",
  parentRelationship: "",

  // Emergency Contact
  emergencyContactName: "",
  emergencyContactPhone: "",
  emergencyContactRelationship: "",

  // Family Information
  fatherSurname: "",
  fatherGivenNames: "",
  fatherDOB: "",
  fatherInUS: "",
  motherSurname: "",
  motherGivenNames: "",
  motherDOB: "",
  motherInUS: "",
  hasRelativesInUS: "",
  relativeInfo: "",
  spouseInfo: "",

  // Work/Education Information
  primaryOccupation: "",
  employerSchoolName: "",
  employerSchoolAddress: "",
  educationStartDate: "",
  monthlySalary: "",
  jobDuties: "",
  languagesSpoken: "",
  countriesTraveledLast5Years: "",

  // Security Questions
  hasCommunicableDisease: "",
  hasMentalDisorder: "",
  isDrugAbuser: "",
  hasBeenArrested: "",
  hasViolatedDrugLaw: "",
  hasEngagedInProstitution: "",
  hasEngagedInMoneyLaundering: "",
  hasEngagedInEspionage: "",
  hasEngagedInTerrorism: "",
  hasProvidedTerroristSupport: "",
  isTerroristMember: "",
  hasEngagedInGenocide: "",
  hasEngagedInTorture: "",
  hasEngagedInViolence: "",
  hasViolatedReligiousFreedom: "",
  hasCommittedVisaFraud: "",
  hasWithheldCustody: "",
  hasVotedIllegally: "",
  hasRenouncedCitizenshipForTax: "",

  // Consent
  consentAcknowledgment: false,
};

const VisaApplication: React.FC = () => {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});
  const [currentStep, setCurrentStep] = useState(1);

  // File uploads
  const [passportCopy, setPassportCopy] = useState<FileUpload | null>(null);
  const [visaPhoto, setVisaPhoto] = useState<FileUpload | null>(null);

  const passportCopyRef = useRef<HTMLInputElement>(null);
  const visaPhotoRef = useRef<HTMLInputElement>(null);

  const totalSteps = 10;

  const stepTitles = [
    "Personal Information",
    "Passport Information",
    "Travel Information",
    "Previous U.S. Travel & Visa",
    "U.S. Contact & Parent Info",
    "Family Information",
    "Work/Education Information",
    "Security & Background",
    "Documents",
    "Review & Submit",
  ];

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: "" }));
    }
    if (error) {
      setError(null);
    }
  };

  const handleRadioChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleFileSelect = async (
    e: React.ChangeEvent<HTMLInputElement>,
    type: "passportCopy" | "visaPhoto",
  ) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    const preview = file.type.startsWith("image/")
      ? URL.createObjectURL(file)
      : "";

    const fileState: FileUpload = {
      file,
      preview,
    };

    if (type === "passportCopy") {
      setPassportCopy(fileState);
    } else {
      setVisaPhoto(fileState);
    }

    e.target.value = "";
  };

  const removeFile = (type: "passportCopy" | "visaPhoto") => {
    if (type === "passportCopy") {
      if (passportCopy?.preview) URL.revokeObjectURL(passportCopy.preview);
      setPassportCopy(null);
    } else {
      if (visaPhoto?.preview) URL.revokeObjectURL(visaPhoto.preview);
      setVisaPhoto(null);
    }
  };

  const validateStep = (step: number): boolean => {
    const errors: { [key: string]: string } = {};

    switch (step) {
      case 1: // Personal Information
        if (!formData.firstName.trim())
          errors.firstName = "First name is required";
        if (!formData.lastName.trim())
          errors.lastName = "Last name is required";
        if (!formData.fullNameNativeLanguage.trim())
          errors.fullNameNativeLanguage =
            "Full name in native language is required";
        if (!formData.sex) errors.sex = "Sex is required";
        if (!formData.maritalStatus)
          errors.maritalStatus = "Marital status is required";
        if (!formData.dateOfBirth)
          errors.dateOfBirth = "Date of birth is required";
        if (!formData.placeOfBirth.trim())
          errors.placeOfBirth = "Place of birth is required";
        if (!formData.nationality.trim())
          errors.nationality = "Nationality is required";
        if (!formData.hasOtherNationalities)
          errors.hasOtherNationalities = "Please select an option";
        if (!formData.nationalIdNumber.trim())
          errors.nationalIdNumber = "National ID number is required";
        if (!formData.hasUSSSN) errors.hasUSSSN = "Please select an option";
        if (!formData.homeAddress.trim())
          errors.homeAddress = "Home address is required";
        if (!formData.studentCity.trim())
          errors.studentCity = "City is required";
        if (!formData.studentCountry.trim())
          errors.studentCountry = "Country is required";
        if (!formData.currentLocationCity.trim())
          errors.currentLocationCity = "Current location city is required";
        if (!formData.currentLocationCountry.trim())
          errors.currentLocationCountry =
            "Current location country is required";
        if (!formData.sameMailingAddress)
          errors.sameMailingAddress = "Please select an option";
        if (!formData.primaryPhone.trim())
          errors.primaryPhone = "Primary phone is required";
        if (!formData.mobilePhone.trim())
          errors.mobilePhone = "Mobile phone is required";
        if (!formData.email.trim()) errors.email = "Email is required";
        else if (!/\S+@\S+\.\S+/.test(formData.email))
          errors.email = "Invalid email format";
        if (!formData.socialMediaLinks.trim())
          errors.socialMediaLinks = "Social media links are required";
        break;

      case 2: // Passport Information
        if (!formData.passportNumber.trim())
          errors.passportNumber = "Passport number is required";
        if (!formData.passportIssuingAuthority.trim())
          errors.passportIssuingAuthority = "Issuing authority is required";
        if (!formData.passportCountryIssued.trim())
          errors.passportCountryIssued = "Country issued is required";
        if (!formData.passportCityIssued.trim())
          errors.passportCityIssued = "City issued is required";
        if (!formData.passportIssueDate)
          errors.passportIssueDate = "Issue date is required";
        if (!formData.passportExpiryDate)
          errors.passportExpiryDate = "Expiry date is required";
        if (!formData.hasLostPassport)
          errors.hasLostPassport = "Please select an option";
        break;

      case 3: // Travel Information
        if (!formData.programName)
          errors.programName = "Program name is required";
        if (!formData.purposeOfTrip)
          errors.purposeOfTrip = "Purpose of trip is required";
        if (!formData.intendedArrivalDate)
          errors.intendedArrivalDate = "Travel start date is required";
        if (!formData.travelEndDate)
          errors.travelEndDate = "Travel end date is required";
        if (!formData.intendedLengthOfStay.trim())
          errors.intendedLengthOfStay = "Length of stay is required";
        if (!formData.usStayAddress.trim())
          errors.usStayAddress = "U.S. stay address is required";
        if (!formData.tripPayingEntity)
          errors.tripPayingEntity = "Please select who is paying";
        if (!formData.tripPayerName.trim())
          errors.tripPayerName = "Payer name is required";
        if (!formData.tripPayerPhone.trim())
          errors.tripPayerPhone = "Payer phone is required";
        if (!formData.tripPayerEmail.trim())
          errors.tripPayerEmail = "Payer email is required";
        if (!formData.tripPayerRelationship)
          errors.tripPayerRelationship = "Relationship is required";
        if (!formData.tripPayerAddressSameAsHome)
          errors.tripPayerAddressSameAsHome = "Please select an option";
        if (
          formData.tripPayerAddressSameAsHome === "No" &&
          !formData.tripPayerAddress.trim()
        )
          errors.tripPayerAddress = "Payer address is required";
        if (!formData.hasTravelCompanions)
          errors.hasTravelCompanions = "Please select an option";
        if (
          formData.hasTravelCompanions === "Yes" &&
          !formData.travelCompanionRelationship.trim()
        )
          errors.travelCompanionRelationship = "Relationship is required";
        break;

      case 4: // Previous U.S. Travel & Visa
        if (!formData.hasBeenToUS)
          errors.hasBeenToUS = "Please select an option";
        if (!formData.hasUSVisa) errors.hasUSVisa = "Please select an option";
        if (formData.hasUSVisa === "Yes" && !formData.usVisaNumber.trim())
          errors.usVisaNumber = "Visa number is required";
        if (!formData.hasBeenRefusedVisa)
          errors.hasBeenRefusedVisa = "Please select an option";
        break;

      case 5: // U.S. Contact Information & Parent/Emergency
        if (!formData.usContactName.trim())
          errors.usContactName = "Contact name is required";
        if (!formData.usContactOrganization.trim())
          errors.usContactOrganization = "Organization name is required";
        if (!formData.usContactRelationship)
          errors.usContactRelationship = "Relationship is required";
        if (!formData.usContactAddress.trim())
          errors.usContactAddress = "Contact address is required";
        if (!formData.usContactPhone.trim())
          errors.usContactPhone = "Contact phone is required";
        if (!formData.usContactEmail.trim())
          errors.usContactEmail = "Contact email is required";
        // Parent/Guardian
        if (!formData.parentName.trim())
          errors.parentName = "Parent/guardian name is required";
        if (!formData.parentEmail.trim())
          errors.parentEmail = "Parent email is required";
        if (!formData.parentPhone.trim())
          errors.parentPhone = "Parent phone is required";
        if (!formData.parentRelationship)
          errors.parentRelationship = "Relationship is required";
        // Emergency Contact
        if (!formData.emergencyContactName.trim())
          errors.emergencyContactName = "Emergency contact name is required";
        if (!formData.emergencyContactPhone.trim())
          errors.emergencyContactPhone = "Emergency contact phone is required";
        if (!formData.emergencyContactRelationship.trim())
          errors.emergencyContactRelationship =
            "Emergency contact relationship is required";
        break;

      case 6: // Family Information
        if (!formData.fatherSurname.trim())
          errors.fatherSurname = "Father's surname is required";
        if (!formData.fatherGivenNames.trim())
          errors.fatherGivenNames = "Father's given names are required";
        if (!formData.fatherDOB)
          errors.fatherDOB = "Father's date of birth is required";
        if (!formData.fatherInUS) errors.fatherInUS = "Please select an option";
        if (!formData.motherSurname.trim())
          errors.motherSurname = "Mother's surname is required";
        if (!formData.motherGivenNames.trim())
          errors.motherGivenNames = "Mother's given names are required";
        if (!formData.motherDOB)
          errors.motherDOB = "Mother's date of birth is required";
        if (!formData.motherInUS) errors.motherInUS = "Please select an option";
        if (!formData.hasRelativesInUS)
          errors.hasRelativesInUS = "Please select an option";
        if (
          formData.hasRelativesInUS === "Yes" &&
          !formData.relativeInfo.trim()
        )
          errors.relativeInfo = "Relative information is required";
        break;

      case 7: // Work/Education Information
        if (!formData.primaryOccupation)
          errors.primaryOccupation = "Occupation is required";
        if (!formData.employerSchoolName.trim())
          errors.employerSchoolName = "Employer/School name is required";
        if (!formData.employerSchoolAddress.trim())
          errors.employerSchoolAddress = "Address is required";
        if (!formData.educationStartDate)
          errors.educationStartDate = "Start date is required";
        if (!formData.jobDuties.trim())
          errors.jobDuties = "Duties description is required";
        if (!formData.languagesSpoken.trim())
          errors.languagesSpoken = "Languages spoken are required";
        break;

      case 8: // Security Questions
        const securityFields = [
          "hasCommunicableDisease",
          "hasMentalDisorder",
          "isDrugAbuser",
          "hasBeenArrested",
          "hasViolatedDrugLaw",
          "hasEngagedInProstitution",
          "hasEngagedInMoneyLaundering",
          "hasEngagedInEspionage",
          "hasEngagedInTerrorism",
          "hasProvidedTerroristSupport",
          "isTerroristMember",
          "hasEngagedInGenocide",
          "hasEngagedInTorture",
          "hasEngagedInViolence",
          "hasViolatedReligiousFreedom",
          "hasCommittedVisaFraud",
          "hasWithheldCustody",
          "hasVotedIllegally",
          "hasRenouncedCitizenshipForTax",
        ];
        securityFields.forEach((field) => {
          if (!formData[field as keyof FormData]) {
            errors[field] = "Please select an option";
          }
        });
        break;

      case 9: // Documents
        if (!passportCopy?.file)
          errors.passportCopy = "Passport bio page is required";
        if (!visaPhoto?.file) errors.visaPhoto = "Visa photo is required";
        break;

      case 10: // Review & Submit
        if (!formData.consentAcknowledgment)
          errors.consentAcknowledgment = "You must accept the terms to submit";
        break;
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, totalSteps));
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handlePrevious = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateStep(currentStep)) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const API_URL = process.env.REACT_APP_API_BASE_URL;

      // Create FormData for multipart upload
      const formDataToSend = new FormData();

      // Map frontend fields to backend expected field names
      const fieldMapping: Record<string, string> = {
        // Personal Information
        firstName: "student_first_name",
        lastName: "student_last_name",
        fullNameNativeLanguage: "full_name_native_language",
        sex: "student_gender",
        maritalStatus: "marital_status",
        dateOfBirth: "student_date_of_birth",
        placeOfBirth: "place_of_birth",
        nationality: "student_nationality",
        hasOtherNationalities: "has_other_nationalities",
        nationalIdNumber: "national_id_number",
        hasUSSSN: "has_us_ssn",
        usSSNNumber: "us_ssn_number",
        homeAddress: "student_address",
        sameMailingAddress: "same_mailing_address",
        primaryPhone: "student_phone",
        mobilePhone: "mobile_phone",
        email: "student_email",
        socialMediaLinks: "social_media_links",

        // Location fields
        currentLocation: "current_location",
        studentCity: "student_city",
        studentCountry: "student_country",
        currentLocationCity: "current_location_city",
        currentLocationCountry: "current_location_country",

        // Passport Information
        passportNumber: "passport_number",
        passportIssuingAuthority: "passport_issuing_authority",
        passportCountryIssued: "passport_issue_country",
        passportCityIssued: "passport_city_issued",
        passportIssueDate: "passport_issue_date",
        passportExpiryDate: "passport_expiry_date",
        hasLostPassport: "has_lost_passport",

        // Travel Information
        purposeOfTrip: "purpose_of_trip",
        intendedArrivalDate: "travel_start_date",
        intendedLengthOfStay: "intended_length_of_stay",
        travelEndDate: "travel_end_date",
        usStayAddress: "us_stay_address",
        tripPayingEntity: "trip_paying_entity",
        tripPayerName: "trip_payer_name",
        tripPayerPhone: "trip_payer_phone",
        tripPayerEmail: "trip_payer_email",
        tripPayerRelationship: "trip_payer_relationship",
        tripPayerAddressSameAsHome: "trip_payer_address_same_as_home",
        tripPayerAddress: "trip_payer_address",
        hasTravelCompanions: "has_travel_companions",
        travelCompanionRelationship: "travel_companion_relationship",

        // Program
        programName: "program_name",

        // Previous U.S. Travel & Visa
        hasBeenToUS: "previous_us_travel",
        hasUSVisa: "previous_us_visa",
        usVisaNumber: "us_visa_number",
        hasBeenRefusedVisa: "has_been_refused_visa",

        // U.S. Contact Information
        usContactName: "us_contact_name",
        usContactOrganization: "us_contact_organization",
        usContactRelationship: "us_contact_relationship",
        usContactAddress: "us_contact_address",
        usContactPhone: "us_contact_phone",
        usContactEmail: "us_contact_email",

        // Family Information
        fatherSurname: "father_surname",
        fatherGivenNames: "father_given_names",
        fatherDOB: "father_dob",
        fatherInUS: "father_in_us",
        motherSurname: "mother_surname",
        motherGivenNames: "mother_given_names",
        motherDOB: "mother_dob",
        motherInUS: "mother_in_us",
        hasRelativesInUS: "has_relatives_in_us",
        relativeInfo: "relative_info",
        spouseInfo: "spouse_info",

        // Parent/Guardian Information
        parentName: "parent_name",
        parentEmail: "parent_email",
        parentPhone: "parent_phone",
        parentRelationship: "parent_relationship",

        // Emergency Contact
        emergencyContactName: "emergency_contact_name",
        emergencyContactPhone: "emergency_contact_phone",
        emergencyContactRelationship: "emergency_contact_relationship",

        // Work/Education Information
        primaryOccupation: "primary_occupation",
        employerSchoolName: "employer_school_name",
        employerSchoolAddress: "employer_school_address",
        educationStartDate: "education_start_date",
        monthlySalary: "monthly_salary",
        jobDuties: "job_duties",
        languagesSpoken: "languages_spoken",
        countriesTraveledLast5Years: "countries_traveled_last_5_years",

        // Security Questions
        hasCommunicableDisease: "has_communicable_disease",
        hasMentalDisorder: "has_mental_disorder",
        isDrugAbuser: "is_drug_abuser",
        hasBeenArrested: "has_been_arrested",
        hasViolatedDrugLaw: "has_violated_drug_law",
        hasEngagedInProstitution: "has_engaged_in_prostitution",
        hasEngagedInMoneyLaundering: "has_engaged_in_money_laundering",
        hasEngagedInEspionage: "has_engaged_in_espionage",
        hasEngagedInTerrorism: "has_engaged_in_terrorism",
        hasProvidedTerroristSupport: "has_provided_terrorist_support",
        isTerroristMember: "is_terrorist_member",
        hasEngagedInGenocide: "has_engaged_in_genocide",
        hasEngagedInTorture: "has_engaged_in_torture",
        hasEngagedInViolence: "has_engaged_in_violence",
        hasViolatedReligiousFreedom: "has_violated_religious_freedom",
        hasCommittedVisaFraud: "has_committed_visa_fraud",
        hasWithheldCustody: "has_withheld_custody",
        hasVotedIllegally: "has_voted_illegally",
        hasRenouncedCitizenshipForTax: "has_renounced_citizenship_for_tax",

        // Consent
        consentAcknowledgment: "consent_acknowledgment",
      };

      // Add all form fields with proper backend field names
      Object.entries(formData).forEach(([key, value]) => {
        const backendKey = fieldMapping[key] || key;
        if (typeof value === "boolean") {
          // Backend expects 1/0 for boolean fields
          formDataToSend.append(backendKey, value ? "1" : "0");
        } else if (value !== null && value !== undefined && value !== "") {
          formDataToSend.append(backendKey, String(value));
        }
      });

      // Add timestamp
      formDataToSend.append("submitted_at", new Date().toISOString());

      // Add files
      if (passportCopy?.file) {
        formDataToSend.append("passport_copy", passportCopy.file);
      }
      if (visaPhoto?.file) {
        formDataToSend.append("passport_photo", visaPhoto.file);
        formDataToSend.append("visa_photo", visaPhoto.file);
      }

      const response = await fetch(`${API_URL}/visa-applications`, {
        method: "POST",
        body: formDataToSend,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        console.error("Backend validation errors:", errorData);
        throw new Error(
          errorData?.detail?.[0]?.msg ||
            errorData?.message ||
            `Submission failed (${response.status})`,
        );
      }

      setIsSubmitted(true);
    } catch (err) {
      console.error("Submission error:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to submit application. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const getInputClasses = (fieldName: string) => {
    const baseClasses =
      "w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all duration-200";

    if (fieldErrors[fieldName]) {
      return `${baseClasses} border-red-500 focus:ring-red-400`;
    }

    return baseClasses;
  };

  const renderRadioGroup = (
    name: string,
    options: string[],
    label: string,
    displayLabels?: string[],
    required: boolean = true,
  ) => (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-200">
        {label}
        {required && <span className="text-red-400 ml-1">*</span>}
      </label>
      <div className="flex flex-wrap gap-4">
        {options.map((option, index) => (
          <label
            key={option}
            className="flex items-center space-x-2 cursor-pointer"
          >
            <input
              type="radio"
              name={name}
              value={option}
              checked={formData[name as keyof FormData] === option}
              onChange={() => handleRadioChange(name, option)}
              className="w-4 h-4 text-blue-500 bg-gray-800 border-gray-600 focus:ring-blue-500"
            />
            <span className="text-gray-300">
              {displayLabels ? displayLabels[index] : option}
            </span>
          </label>
        ))}
      </div>
      {fieldErrors[name] && (
        <p className="text-red-400 text-sm flex items-center gap-1">
          <AlertCircle className="w-4 h-4" />
          {fieldErrors[name]}
        </p>
      )}
    </div>
  );

  const renderFileUpload = (
    type: "passportCopy" | "visaPhoto",
    upload: FileUpload | null,
    inputRef: React.RefObject<HTMLInputElement>,
    label: string,
    description: string,
    accept: string,
  ) => (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-200">
        {label}
        <span className="text-red-400 ml-1">*</span>
      </label>
      <p className="text-sm text-gray-400">{description}</p>

      {upload ? (
        <div className="relative p-4 bg-gray-800/50 border border-gray-700 rounded-xl">
          <div className="flex items-center gap-3">
            {upload.preview ? (
              <img
                src={upload.preview}
                alt="Preview"
                className="w-16 h-16 object-cover rounded-lg"
              />
            ) : (
              <div className="w-16 h-16 bg-gray-700 rounded-lg flex items-center justify-center">
                <FileText className="w-8 h-8 text-gray-400" />
              </div>
            )}
            <div className="flex-1">
              <p className="text-white font-medium truncate">
                {upload.file.name}
              </p>
              <p className="text-sm text-gray-400">
                {(upload.file.size / 1024 / 1024).toFixed(2)} MB
              </p>
              <div className="flex items-center gap-2 text-green-400">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm">Ready to upload</span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => removeFile(type)}
              className="p-2 text-gray-400 hover:text-red-400 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="w-full p-6 border-2 border-dashed border-gray-600 rounded-xl hover:border-blue-400 transition-colors flex flex-col items-center gap-2"
        >
          <Upload className="w-8 h-8 text-gray-400" />
          <span className="text-gray-400">Click to upload</span>
        </button>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={(e) => handleFileSelect(e, type)}
        className="hidden"
      />

      {fieldErrors[type] && (
        <p className="text-red-400 text-sm flex items-center gap-1">
          <AlertCircle className="w-4 h-4" />
          {fieldErrors[type]}
        </p>
      )}
    </div>
  );

  const renderStepIndicator = () => {
    const progress = ((currentStep - 1) / (totalSteps - 1)) * 100;

    return (
      <div className="mb-8 max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm text-gray-400">
            Step {currentStep} of {totalSteps}
          </span>
          <span className="text-sm font-medium text-blue-400">
            {stepTitles[currentStep - 1]}
          </span>
        </div>

        <div className="relative h-2 bg-gray-700 rounded-full overflow-hidden">
          <div
            className="absolute left-0 top-0 h-full bg-gradient-to-r from-blue-500 to-blue-400 rounded-full transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className="flex justify-between mt-2">
          {Array.from({ length: totalSteps }, (_, i) => i + 1).map((step) => (
            <div
              key={step}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                step === currentStep
                  ? "bg-blue-500 scale-150"
                  : step < currentStep
                    ? "bg-green-500"
                    : "bg-gray-600"
              }`}
              title={`Step ${step}: ${stepTitles[step - 1]}`}
            />
          ))}
        </div>
      </div>
    );
  };

  // Step 1: Personal Information
  const renderStep1 = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-white mb-6">
        Personal Information
      </h2>

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-200 mb-2">
            First Name (as listed in passport){" "}
            <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            name="firstName"
            value={formData.firstName}
            onChange={handleInputChange}
            placeholder="Enter your first name"
            className={getInputClasses("firstName")}
          />
          {fieldErrors.firstName && (
            <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {fieldErrors.firstName}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-200 mb-2">
            Last Name (as listed in passport){" "}
            <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            name="lastName"
            value={formData.lastName}
            onChange={handleInputChange}
            placeholder="Enter your last name"
            className={getInputClasses("lastName")}
          />
          {fieldErrors.lastName && (
            <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {fieldErrors.lastName}
            </p>
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-200 mb-2">
          Full Name in Native Language <span className="text-red-400">*</span>
        </label>
        <input
          type="text"
          name="fullNameNativeLanguage"
          value={formData.fullNameNativeLanguage}
          onChange={handleInputChange}
          placeholder="Enter your full name in native language"
          className={getInputClasses("fullNameNativeLanguage")}
        />
        {fieldErrors.fullNameNativeLanguage && (
          <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {fieldErrors.fullNameNativeLanguage}
          </p>
        )}
      </div>

      {renderRadioGroup("sex", ["Male", "Female"], "Sex")}

      {renderRadioGroup(
        "maritalStatus",
        ["Married", "Single", "Divorced"],
        "Marital Status",
      )}

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-200 mb-2">
            Date of Birth <span className="text-red-400">*</span>
          </label>
          <input
            type="date"
            name="dateOfBirth"
            value={formData.dateOfBirth}
            onChange={handleInputChange}
            className={getInputClasses("dateOfBirth")}
          />
          {fieldErrors.dateOfBirth && (
            <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {fieldErrors.dateOfBirth}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-200 mb-2">
            Place of Birth <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            name="placeOfBirth"
            value={formData.placeOfBirth}
            onChange={handleInputChange}
            placeholder="City, Country"
            className={getInputClasses("placeOfBirth")}
          />
          {fieldErrors.placeOfBirth && (
            <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {fieldErrors.placeOfBirth}
            </p>
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-200 mb-2">
          Nationality <span className="text-red-400">*</span>
        </label>
        <input
          type="text"
          name="nationality"
          value={formData.nationality}
          onChange={handleInputChange}
          placeholder="Enter your nationality"
          className={getInputClasses("nationality")}
        />
        {fieldErrors.nationality && (
          <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {fieldErrors.nationality}
          </p>
        )}
      </div>

      {renderRadioGroup(
        "hasOtherNationalities",
        ["Yes", "No"],
        "Other Nationalities",
      )}

      <div>
        <label className="block text-sm font-medium text-gray-200 mb-2">
          National Identification Number (Original Country){" "}
          <span className="text-red-400">*</span>
        </label>
        <input
          type="text"
          name="nationalIdNumber"
          value={formData.nationalIdNumber}
          onChange={handleInputChange}
          placeholder="Enter your national ID number"
          className={getInputClasses("nationalIdNumber")}
        />
        {fieldErrors.nationalIdNumber && (
          <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {fieldErrors.nationalIdNumber}
          </p>
        )}
      </div>

      {renderRadioGroup(
        "hasUSSSN",
        ["Not Applicable", "Number"],
        "U.S. Social Security Number",
      )}

      {formData.hasUSSSN === "Number" && (
        <div>
          <label className="block text-sm font-medium text-gray-200 mb-2">
            U.S. Social Security Number
          </label>
          <input
            type="text"
            name="usSSNNumber"
            value={formData.usSSNNumber}
            onChange={handleInputChange}
            placeholder="XXX-XX-XXXX"
            className={getInputClasses("usSSNNumber")}
          />
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-200 mb-2">
          Home Address <span className="text-red-400">*</span>
        </label>
        <p className="text-xs text-gray-400 mb-2">
          Building Number, Street Name, Apt Number
        </p>
        <textarea
          name="homeAddress"
          value={formData.homeAddress}
          onChange={handleInputChange}
          placeholder="e.g., 456 Olaya Street, Apt 12B"
          rows={2}
          className={getInputClasses("homeAddress")}
        />
        {fieldErrors.homeAddress && (
          <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {fieldErrors.homeAddress}
          </p>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-200 mb-2">
            City <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            name="studentCity"
            value={formData.studentCity}
            onChange={handleInputChange}
            placeholder="Enter your city"
            className={getInputClasses("studentCity")}
          />
          {fieldErrors.studentCity && (
            <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {fieldErrors.studentCity}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-200 mb-2">
            Country <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            name="studentCountry"
            value={formData.studentCountry}
            onChange={handleInputChange}
            placeholder="Enter your country"
            className={getInputClasses("studentCountry")}
          />
          {fieldErrors.studentCountry && (
            <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {fieldErrors.studentCountry}
            </p>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-200 mb-2">
            Current Location City <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            name="currentLocationCity"
            value={formData.currentLocationCity}
            onChange={handleInputChange}
            placeholder="City where you currently reside"
            className={getInputClasses("currentLocationCity")}
          />
          {fieldErrors.currentLocationCity && (
            <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {fieldErrors.currentLocationCity}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-200 mb-2">
            Current Location Country <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            name="currentLocationCountry"
            value={formData.currentLocationCountry}
            onChange={handleInputChange}
            placeholder="Country where you currently reside"
            className={getInputClasses("currentLocationCountry")}
          />
          {fieldErrors.currentLocationCountry && (
            <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {fieldErrors.currentLocationCountry}
            </p>
          )}
        </div>
      </div>

      {renderRadioGroup(
        "sameMailingAddress",
        ["Yes", "No"],
        "Is Your Mailing Address the Same as Home Address?",
      )}

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-200 mb-2">
            Primary Phone Number <span className="text-red-400">*</span>
          </label>
          <input
            type="tel"
            name="primaryPhone"
            value={formData.primaryPhone}
            onChange={handleInputChange}
            placeholder="+1234567890"
            className={getInputClasses("primaryPhone")}
          />
          {fieldErrors.primaryPhone && (
            <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {fieldErrors.primaryPhone}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-200 mb-2">
            Mobile/Cell Phone Number <span className="text-red-400">*</span>
          </label>
          <input
            type="tel"
            name="mobilePhone"
            value={formData.mobilePhone}
            onChange={handleInputChange}
            placeholder="+1234567890"
            className={getInputClasses("mobilePhone")}
          />
          {fieldErrors.mobilePhone && (
            <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {fieldErrors.mobilePhone}
            </p>
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-200 mb-2">
          Email Address (should be unique for each application){" "}
          <span className="text-red-400">*</span>
        </label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleInputChange}
          placeholder="your.email@example.com"
          className={getInputClasses("email")}
        />
        {fieldErrors.email && (
          <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {fieldErrors.email}
          </p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-200 mb-2">
          Social Media Links (strongly recommended){" "}
          <span className="text-red-400">*</span>
        </label>
        <textarea
          name="socialMediaLinks"
          value={formData.socialMediaLinks}
          onChange={handleInputChange}
          placeholder="Facebook, Instagram, Twitter, LinkedIn URLs"
          rows={3}
          className={getInputClasses("socialMediaLinks")}
        />
        {fieldErrors.socialMediaLinks && (
          <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {fieldErrors.socialMediaLinks}
          </p>
        )}
      </div>
    </div>
  );

  // Step 2: Passport Information
  const renderStep2 = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-white mb-6">
        Passport Information
      </h2>

      <div>
        <label className="block text-sm font-medium text-gray-200 mb-2">
          Passport Number <span className="text-red-400">*</span>
        </label>
        <input
          type="text"
          name="passportNumber"
          value={formData.passportNumber}
          onChange={handleInputChange}
          placeholder="Enter your passport number"
          className={getInputClasses("passportNumber")}
        />
        {fieldErrors.passportNumber && (
          <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {fieldErrors.passportNumber}
          </p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-200 mb-2">
          Country/Authority that Issued Passport{" "}
          <span className="text-red-400">*</span>
        </label>
        <input
          type="text"
          name="passportIssuingAuthority"
          value={formData.passportIssuingAuthority}
          onChange={handleInputChange}
          placeholder="Enter issuing authority"
          className={getInputClasses("passportIssuingAuthority")}
        />
        {fieldErrors.passportIssuingAuthority && (
          <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {fieldErrors.passportIssuingAuthority}
          </p>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-200 mb-2">
            Country Where Issued <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            name="passportCountryIssued"
            value={formData.passportCountryIssued}
            onChange={handleInputChange}
            placeholder="Enter country"
            className={getInputClasses("passportCountryIssued")}
          />
          {fieldErrors.passportCountryIssued && (
            <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {fieldErrors.passportCountryIssued}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-200 mb-2">
            City of the Country Where Issued{" "}
            <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            name="passportCityIssued"
            value={formData.passportCityIssued}
            onChange={handleInputChange}
            placeholder="Enter city"
            className={getInputClasses("passportCityIssued")}
          />
          {fieldErrors.passportCityIssued && (
            <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {fieldErrors.passportCityIssued}
            </p>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-200 mb-2">
            Passport Issuance Date <span className="text-red-400">*</span>
          </label>
          <input
            type="date"
            name="passportIssueDate"
            value={formData.passportIssueDate}
            onChange={handleInputChange}
            className={getInputClasses("passportIssueDate")}
          />
          {fieldErrors.passportIssueDate && (
            <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {fieldErrors.passportIssueDate}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-200 mb-2">
            Expiration Date <span className="text-red-400">*</span>
          </label>
          <input
            type="date"
            name="passportExpiryDate"
            value={formData.passportExpiryDate}
            onChange={handleInputChange}
            className={getInputClasses("passportExpiryDate")}
          />
          {fieldErrors.passportExpiryDate && (
            <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {fieldErrors.passportExpiryDate}
            </p>
          )}
        </div>
      </div>

      {renderRadioGroup(
        "hasLostPassport",
        ["Yes", "No"],
        "Have you ever lost a passport or had one stolen?",
      )}
    </div>
  );

  // Step 3: Travel Information
  const renderStep3 = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-white mb-6">Travel Information</h2>

      <div>
        <label className="block text-sm font-medium text-gray-200 mb-2">
          Program Name <span className="text-red-400">*</span>
        </label>
        <select
          name="programName"
          value={formData.programName}
          onChange={handleInputChange}
          className={getInputClasses("programName")}
        >
          <option value="">Select a program</option>
          <option value="NASA Artemis Program">NASA Artemis Program</option>
          <option value="NASA Kennedy Space Center">
            NASA Kennedy Space Center
          </option>
          <option value="Future CEO Program">Future CEO Program</option>
          <option value="Cambridge Excellence Program">
            Cambridge Excellence Program
          </option>
          <option value="Teacher Development Program">
            Teacher Development Program
          </option>
          <option value="Other">Other</option>
        </select>
        {fieldErrors.programName && (
          <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {fieldErrors.programName}
          </p>
        )}
      </div>

      {renderRadioGroup(
        "purposeOfTrip",
        [
          "TOURIST/B1-B2 Visa",
          "Education/Student Visa",
          "Exchange Program J1 Visa",
        ],
        "Purpose of Your Trip to U.S.",
      )}

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-200 mb-2">
            Travel Start Date <span className="text-red-400">*</span>
          </label>
          <input
            type="date"
            name="intendedArrivalDate"
            value={formData.intendedArrivalDate}
            onChange={handleInputChange}
            className={getInputClasses("intendedArrivalDate")}
          />
          {fieldErrors.intendedArrivalDate && (
            <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {fieldErrors.intendedArrivalDate}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-200 mb-2">
            Travel End Date <span className="text-red-400">*</span>
          </label>
          <input
            type="date"
            name="travelEndDate"
            value={formData.travelEndDate}
            onChange={handleInputChange}
            className={getInputClasses("travelEndDate")}
          />
          {fieldErrors.travelEndDate && (
            <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {fieldErrors.travelEndDate}
            </p>
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-200 mb-2">
          Intended Length of Stay in U.S.{" "}
          <span className="text-red-400">*</span>
        </label>
        <input
          type="text"
          name="intendedLengthOfStay"
          value={formData.intendedLengthOfStay}
          onChange={handleInputChange}
          placeholder="e.g., 2 weeks, 1 month"
          className={getInputClasses("intendedLengthOfStay")}
        />
        {fieldErrors.intendedLengthOfStay && (
          <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {fieldErrors.intendedLengthOfStay}
          </p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-200 mb-2">
          Address where you will stay in the U.S.{" "}
          <span className="text-red-400">*</span>
        </label>
        <p className="text-xs text-gray-400 mb-2">
          NYEA Address found in the NASA Brochure
        </p>
        <input
          type="text"
          name="usStayAddress"
          value={formData.usStayAddress}
          onChange={handleInputChange}
          placeholder="Enter U.S. address"
          className={getInputClasses("usStayAddress")}
        />
        {fieldErrors.usStayAddress && (
          <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {fieldErrors.usStayAddress}
          </p>
        )}
      </div>

      {renderRadioGroup(
        "tripPayingEntity",
        ["Parent", "Myself", "Some other Person"],
        "Person/Entity Paying for Your Trip",
      )}

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-200 mb-2">
            Name of Person Paying for Your Trip{" "}
            <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            name="tripPayerName"
            value={formData.tripPayerName}
            onChange={handleInputChange}
            placeholder="Enter payer's name"
            className={getInputClasses("tripPayerName")}
          />
          {fieldErrors.tripPayerName && (
            <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {fieldErrors.tripPayerName}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-200 mb-2">
            Telephone of Person Paying <span className="text-red-400">*</span>
          </label>
          <input
            type="tel"
            name="tripPayerPhone"
            value={formData.tripPayerPhone}
            onChange={handleInputChange}
            placeholder="+1234567890"
            className={getInputClasses("tripPayerPhone")}
          />
          {fieldErrors.tripPayerPhone && (
            <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {fieldErrors.tripPayerPhone}
            </p>
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-200 mb-2">
          Email Address of Person Paying <span className="text-red-400">*</span>
        </label>
        <input
          type="email"
          name="tripPayerEmail"
          value={formData.tripPayerEmail}
          onChange={handleInputChange}
          placeholder="payer@example.com"
          className={getInputClasses("tripPayerEmail")}
        />
        {fieldErrors.tripPayerEmail && (
          <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {fieldErrors.tripPayerEmail}
          </p>
        )}
      </div>

      {renderRadioGroup(
        "tripPayerRelationship",
        ["Parent", "Child", "Siblings", "Other Relative"],
        "Relationship to You",
      )}

      {renderRadioGroup(
        "tripPayerAddressSameAsHome",
        ["Yes", "No"],
        "Is the address of the party paying for your trip the same as your Home or Mailing Address?",
      )}

      {formData.tripPayerAddressSameAsHome === "No" && (
        <div>
          <label className="block text-sm font-medium text-gray-200 mb-2">
            Payer's Address <span className="text-red-400">*</span>
          </label>
          <p className="text-xs text-gray-400 mb-2">
            City, State/Province, Postal Zone/ZIP Code, Country
          </p>
          <input
            type="text"
            name="tripPayerAddress"
            value={formData.tripPayerAddress}
            onChange={handleInputChange}
            placeholder="Enter full address"
            className={getInputClasses("tripPayerAddress")}
          />
          {fieldErrors.tripPayerAddress && (
            <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {fieldErrors.tripPayerAddress}
            </p>
          )}
        </div>
      )}

      {renderRadioGroup(
        "hasTravelCompanions",
        ["Yes", "No"],
        "Other Persons Traveling with You",
      )}

      {formData.hasTravelCompanions === "Yes" && (
        <div>
          <label className="block text-sm font-medium text-gray-200 mb-2">
            Relationship to You <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            name="travelCompanionRelationship"
            value={formData.travelCompanionRelationship}
            onChange={handleInputChange}
            placeholder="e.g., Parent, Sibling, Friend"
            className={getInputClasses("travelCompanionRelationship")}
          />
          {fieldErrors.travelCompanionRelationship && (
            <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {fieldErrors.travelCompanionRelationship}
            </p>
          )}
        </div>
      )}
    </div>
  );

  // Step 4: Previous U.S. Travel & Visa
  const renderStep4 = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-white mb-6">
        Previous U.S. Travel & Visa
      </h2>

      {renderRadioGroup(
        "hasBeenToUS",
        ["Yes", "No"],
        "Have you ever been in the U.S.?",
      )}

      {renderRadioGroup(
        "hasUSVisa",
        ["Yes", "No"],
        "Have you ever been issued a U.S. visa?",
      )}

      {formData.hasUSVisa === "Yes" && (
        <div>
          <label className="block text-sm font-medium text-gray-200 mb-2">
            Visa Number <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            name="usVisaNumber"
            value={formData.usVisaNumber}
            onChange={handleInputChange}
            placeholder="Enter visa number"
            className={getInputClasses("usVisaNumber")}
          />
          {fieldErrors.usVisaNumber && (
            <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {fieldErrors.usVisaNumber}
            </p>
          )}
        </div>
      )}

      {renderRadioGroup(
        "hasBeenRefusedVisa",
        ["Yes", "No"],
        "Have you ever been refused a U.S. Visa, been refused admission to the United States, or withdrawn your application for admission at the point of entry?",
      )}
    </div>
  );

  // Step 5: U.S. Contact Information
  const renderStep5 = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-white mb-6">
        U.S. Contact Information
      </h2>

      <div>
        <label className="block text-sm font-medium text-gray-200 mb-2">
          Contact Person Name in the U.S.{" "}
          <span className="text-red-400">*</span>
        </label>
        <p className="text-xs text-gray-400 mb-2">e.g., Sir Fatih Kahraman</p>
        <input
          type="text"
          name="usContactName"
          value={formData.usContactName}
          onChange={handleInputChange}
          placeholder="Enter contact name"
          className={getInputClasses("usContactName")}
        />
        {fieldErrors.usContactName && (
          <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {fieldErrors.usContactName}
          </p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-200 mb-2">
          Organization Name in the U.S. <span className="text-red-400">*</span>
        </label>
        <p className="text-xs text-gray-400 mb-2">e.g., NYEA</p>
        <input
          type="text"
          name="usContactOrganization"
          value={formData.usContactOrganization}
          onChange={handleInputChange}
          placeholder="Enter organization name"
          className={getInputClasses("usContactOrganization")}
        />
        {fieldErrors.usContactOrganization && (
          <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {fieldErrors.usContactOrganization}
          </p>
        )}
      </div>

      {renderRadioGroup(
        "usContactRelationship",
        ["Person", "School Official", "Company"],
        "Relationship to You",
      )}

      <div>
        <label className="block text-sm font-medium text-gray-200 mb-2">
          U.S. Contact Address <span className="text-red-400">*</span>
        </label>
        <input
          type="text"
          name="usContactAddress"
          value={formData.usContactAddress}
          onChange={handleInputChange}
          placeholder="Enter full address"
          className={getInputClasses("usContactAddress")}
        />
        {fieldErrors.usContactAddress && (
          <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {fieldErrors.usContactAddress}
          </p>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-200 mb-2">
            U.S. Contact Phone Number <span className="text-red-400">*</span>
          </label>
          <input
            type="tel"
            name="usContactPhone"
            value={formData.usContactPhone}
            onChange={handleInputChange}
            placeholder="+1234567890"
            className={getInputClasses("usContactPhone")}
          />
          {fieldErrors.usContactPhone && (
            <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {fieldErrors.usContactPhone}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-200 mb-2">
            U.S. Contact Email Address <span className="text-red-400">*</span>
          </label>
          <input
            type="email"
            name="usContactEmail"
            value={formData.usContactEmail}
            onChange={handleInputChange}
            placeholder="contact@example.com"
            className={getInputClasses("usContactEmail")}
          />
          {fieldErrors.usContactEmail && (
            <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {fieldErrors.usContactEmail}
            </p>
          )}
        </div>
      </div>

      <h3 className="text-xl font-semibold text-blue-400 pt-6">
        Parent/Guardian Information
      </h3>

      <div>
        <label className="block text-sm font-medium text-gray-200 mb-2">
          Parent/Guardian Full Name <span className="text-red-400">*</span>
        </label>
        <input
          type="text"
          name="parentName"
          value={formData.parentName}
          onChange={handleInputChange}
          placeholder="Enter parent/guardian name"
          className={getInputClasses("parentName")}
        />
        {fieldErrors.parentName && (
          <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {fieldErrors.parentName}
          </p>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-200 mb-2">
            Parent/Guardian Email <span className="text-red-400">*</span>
          </label>
          <input
            type="email"
            name="parentEmail"
            value={formData.parentEmail}
            onChange={handleInputChange}
            placeholder="parent@example.com"
            className={getInputClasses("parentEmail")}
          />
          {fieldErrors.parentEmail && (
            <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {fieldErrors.parentEmail}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-200 mb-2">
            Parent/Guardian Phone <span className="text-red-400">*</span>
          </label>
          <input
            type="tel"
            name="parentPhone"
            value={formData.parentPhone}
            onChange={handleInputChange}
            placeholder="+1234567890"
            className={getInputClasses("parentPhone")}
          />
          {fieldErrors.parentPhone && (
            <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {fieldErrors.parentPhone}
            </p>
          )}
        </div>
      </div>

      {renderRadioGroup(
        "parentRelationship",
        ["Mother", "Father", "Guardian", "Other"],
        "Relationship to Student",
      )}

      <h3 className="text-xl font-semibold text-blue-400 pt-6">
        Emergency Contact
      </h3>

      <div>
        <label className="block text-sm font-medium text-gray-200 mb-2">
          Emergency Contact Name <span className="text-red-400">*</span>
        </label>
        <input
          type="text"
          name="emergencyContactName"
          value={formData.emergencyContactName}
          onChange={handleInputChange}
          placeholder="Enter emergency contact name"
          className={getInputClasses("emergencyContactName")}
        />
        {fieldErrors.emergencyContactName && (
          <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {fieldErrors.emergencyContactName}
          </p>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-200 mb-2">
            Emergency Contact Phone <span className="text-red-400">*</span>
          </label>
          <input
            type="tel"
            name="emergencyContactPhone"
            value={formData.emergencyContactPhone}
            onChange={handleInputChange}
            placeholder="+1234567890"
            className={getInputClasses("emergencyContactPhone")}
          />
          {fieldErrors.emergencyContactPhone && (
            <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {fieldErrors.emergencyContactPhone}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-200 mb-2">
            Emergency Contact Relationship{" "}
            <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            name="emergencyContactRelationship"
            value={formData.emergencyContactRelationship}
            onChange={handleInputChange}
            placeholder="e.g., Uncle, Aunt, Friend"
            className={getInputClasses("emergencyContactRelationship")}
          />
          {fieldErrors.emergencyContactRelationship && (
            <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {fieldErrors.emergencyContactRelationship}
            </p>
          )}
        </div>
      </div>
    </div>
  );

  // Step 6: Family Information
  const renderStep6 = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-white mb-6">Family Information</h2>

      <h3 className="text-xl font-semibold text-blue-400">
        Father's Information
      </h3>

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-200 mb-2">
            Father's Surname <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            name="fatherSurname"
            value={formData.fatherSurname}
            onChange={handleInputChange}
            placeholder="Enter surname"
            className={getInputClasses("fatherSurname")}
          />
          {fieldErrors.fatherSurname && (
            <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {fieldErrors.fatherSurname}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-200 mb-2">
            Father's Given Names <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            name="fatherGivenNames"
            value={formData.fatherGivenNames}
            onChange={handleInputChange}
            placeholder="Enter given names"
            className={getInputClasses("fatherGivenNames")}
          />
          {fieldErrors.fatherGivenNames && (
            <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {fieldErrors.fatherGivenNames}
            </p>
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-200 mb-2">
          Father's Date of Birth <span className="text-red-400">*</span>
        </label>
        <input
          type="date"
          name="fatherDOB"
          value={formData.fatherDOB}
          onChange={handleInputChange}
          className={getInputClasses("fatherDOB")}
        />
        {fieldErrors.fatherDOB && (
          <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {fieldErrors.fatherDOB}
          </p>
        )}
      </div>

      {renderRadioGroup(
        "fatherInUS",
        ["Yes", "No"],
        "Is your father in the U.S.?",
      )}

      <h3 className="text-xl font-semibold text-blue-400 pt-4">
        Mother's Information
      </h3>

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-200 mb-2">
            Mother's Surname <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            name="motherSurname"
            value={formData.motherSurname}
            onChange={handleInputChange}
            placeholder="Enter surname"
            className={getInputClasses("motherSurname")}
          />
          {fieldErrors.motherSurname && (
            <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {fieldErrors.motherSurname}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-200 mb-2">
            Mother's Given Names <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            name="motherGivenNames"
            value={formData.motherGivenNames}
            onChange={handleInputChange}
            placeholder="Enter given names"
            className={getInputClasses("motherGivenNames")}
          />
          {fieldErrors.motherGivenNames && (
            <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {fieldErrors.motherGivenNames}
            </p>
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-200 mb-2">
          Mother's Date of Birth <span className="text-red-400">*</span>
        </label>
        <input
          type="date"
          name="motherDOB"
          value={formData.motherDOB}
          onChange={handleInputChange}
          className={getInputClasses("motherDOB")}
        />
        {fieldErrors.motherDOB && (
          <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {fieldErrors.motherDOB}
          </p>
        )}
      </div>

      {renderRadioGroup(
        "motherInUS",
        ["Yes", "No"],
        "Is your mother in the U.S.?",
      )}

      <h3 className="text-xl font-semibold text-blue-400 pt-4">
        Relatives in U.S.
      </h3>

      {renderRadioGroup(
        "hasRelativesInUS",
        ["Yes", "No"],
        "Do you have any immediate relatives, not including parents in the U.S.?",
      )}

      {formData.hasRelativesInUS === "Yes" && (
        <div>
          <label className="block text-sm font-medium text-gray-200 mb-2">
            Relative Information <span className="text-red-400">*</span>
          </label>
          <p className="text-xs text-gray-400 mb-2">
            Name, Relationship to you, Status
          </p>
          <textarea
            name="relativeInfo"
            value={formData.relativeInfo}
            onChange={handleInputChange}
            placeholder="e.g., John Doe, Uncle, U.S. Citizen"
            rows={3}
            className={getInputClasses("relativeInfo")}
          />
          {fieldErrors.relativeInfo && (
            <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {fieldErrors.relativeInfo}
            </p>
          )}
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-200 mb-2">
          Spouse Information (if married)
        </label>
        <p className="text-xs text-gray-400 mb-2">
          Full Name, Date of Birth, Nationality, City of Birth, Country of
          Birth, Address
        </p>
        <textarea
          name="spouseInfo"
          value={formData.spouseInfo}
          onChange={handleInputChange}
          placeholder="Leave blank if not applicable"
          rows={3}
          className={getInputClasses("spouseInfo")}
        />
      </div>
    </div>
  );

  // Step 7: Work/Education Information
  const renderStep7 = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-white mb-6">
        Work / Education / Training Information
      </h2>

      {renderRadioGroup(
        "primaryOccupation",
        ["Student", "Business"],
        "Primary Occupation",
      )}

      <div>
        <label className="block text-sm font-medium text-gray-200 mb-2">
          Present Employer or School Name (for students){" "}
          <span className="text-red-400">*</span>
        </label>
        <input
          type="text"
          name="employerSchoolName"
          value={formData.employerSchoolName}
          onChange={handleInputChange}
          placeholder="Enter employer or school name"
          className={getInputClasses("employerSchoolName")}
        />
        {fieldErrors.employerSchoolName && (
          <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {fieldErrors.employerSchoolName}
          </p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-200 mb-2">
          Address <span className="text-red-400">*</span>
        </label>
        <p className="text-xs text-gray-400 mb-2">
          City, State/Province, Postal Zone/Zip Code, Country
        </p>
        <textarea
          name="employerSchoolAddress"
          value={formData.employerSchoolAddress}
          onChange={handleInputChange}
          placeholder="Enter full address"
          rows={3}
          className={getInputClasses("employerSchoolAddress")}
        />
        {fieldErrors.employerSchoolAddress && (
          <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {fieldErrors.employerSchoolAddress}
          </p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-200 mb-2">
          Start date of education in the school{" "}
          <span className="text-red-400">*</span>
        </label>
        <input
          type="date"
          name="educationStartDate"
          value={formData.educationStartDate}
          onChange={handleInputChange}
          className={getInputClasses("educationStartDate")}
        />
        {fieldErrors.educationStartDate && (
          <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {fieldErrors.educationStartDate}
          </p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-200 mb-2">
          Monthly Salary in Local Currency
        </label>
        <input
          type="text"
          name="monthlySalary"
          value={formData.monthlySalary}
          onChange={handleInputChange}
          placeholder="Enter amount (optional)"
          className={getInputClasses("monthlySalary")}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-200 mb-2">
          Briefly Describe your Duties <span className="text-red-400">*</span>
        </label>
        <textarea
          name="jobDuties"
          value={formData.jobDuties}
          onChange={handleInputChange}
          placeholder="Describe your duties/responsibilities"
          rows={4}
          className={getInputClasses("jobDuties")}
        />
        {fieldErrors.jobDuties && (
          <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {fieldErrors.jobDuties}
          </p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-200 mb-2">
          Provide the List of Languages You Speak{" "}
          <span className="text-red-400">*</span>
        </label>
        <textarea
          name="languagesSpoken"
          value={formData.languagesSpoken}
          onChange={handleInputChange}
          placeholder="e.g., English, Arabic, French"
          rows={2}
          className={getInputClasses("languagesSpoken")}
        />
        {fieldErrors.languagesSpoken && (
          <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {fieldErrors.languagesSpoken}
          </p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-200 mb-2">
          Have you traveled to any countries or regions within the last five
          years?
        </label>
        <p className="text-xs text-gray-400 mb-2">
          If yes, please provide the list. If no, please write "NO"
        </p>
        <textarea
          name="countriesTraveledLast5Years"
          value={formData.countriesTraveledLast5Years}
          onChange={handleInputChange}
          placeholder="e.g., UAE, Turkey, Egypt or NO"
          rows={3}
          className={getInputClasses("countriesTraveledLast5Years")}
        />
      </div>
    </div>
  );

  // Step 8: Security & Background Questions
  const renderStep8 = () => {
    const securityQuestions = [
      {
        name: "hasCommunicableDisease",
        question:
          "Do you have a communicable disease of public health significance such as tuberculosis (TB)?",
      },
      {
        name: "hasMentalDisorder",
        question:
          "Do you have a mental or physical disorder that poses or is likely to pose a threat to the safety or welfare of yourself or others?",
      },
      {
        name: "isDrugAbuser",
        question: "Are you or have you ever been a drug abuser or addict?",
      },
      {
        name: "hasBeenArrested",
        question:
          "Have you ever been arrested or convicted for any offense or crime, even though subject of a pardon, amnesty, or other similar action?",
      },
      {
        name: "hasViolatedDrugLaw",
        question:
          "Have you ever violated, or engaged in a conspiracy to violate, any law relating to controlled substances?",
      },
      {
        name: "hasEngagedInProstitution",
        question:
          "Are you coming to the United States to engage in prostitution or unlawful commercialized vice or have you been engaged in prostitution or procuring prostitutes within the past 10 years?",
      },
      {
        name: "hasEngagedInMoneyLaundering",
        question:
          "Have you ever been involved in, or do you seek to engage in, money laundering?",
      },
      {
        name: "hasEngagedInEspionage",
        question:
          "Do you seek to engage in espionage, sabotage, export control violations, or any other illegal activity while in the United States?",
      },
      {
        name: "hasEngagedInTerrorism",
        question:
          "Do you seek to engage in terrorist activities while in the United States or have you ever engaged in terrorist activities?",
      },
      {
        name: "hasProvidedTerroristSupport",
        question:
          "Have you ever or do you intend to provide financial assistance or other support to terrorists or terrorist organizations?",
      },
      {
        name: "isTerroristMember",
        question:
          "Are you a member or representative of a terrorist organization?",
      },
      {
        name: "hasEngagedInGenocide",
        question:
          "Have you ever ordered, incited, committed, assisted, or otherwise participated in genocide?",
      },
      {
        name: "hasEngagedInTorture",
        question:
          "Have you ever committed, ordered, incited, assisted, or otherwise participated in torture?",
      },
      {
        name: "hasEngagedInViolence",
        question:
          "Have you committed, ordered, incited, assisted, or otherwise participated in extrajudicial killings, political killings, or other acts of violence?",
      },
      {
        name: "hasViolatedReligiousFreedom",
        question:
          "Have you, while serving as a government official, been responsible for or directly carried out, at any time, particularly severe violations of religious freedom?",
      },
      {
        name: "hasCommittedVisaFraud",
        question:
          "Have you ever sought to obtain or assist others to obtain a visa, entry into the United States, or any other United States immigration benefit by fraud or willful misrepresentation or other unlawful means?",
      },
      {
        name: "hasWithheldCustody",
        question:
          "Have you ever withheld custody of a U.S. citizen child outside the United States from a person granted legal custody by a U.S. court?",
      },
      {
        name: "hasVotedIllegally",
        question:
          "Have you voted in the United States in violation of any law or regulation?",
      },
      {
        name: "hasRenouncedCitizenshipForTax",
        question:
          "Have you ever renounced United States citizenship for the purpose of avoiding taxation?",
      },
    ];

    return (
      <div className="space-y-6">
        <h2 className="text-2xl font-bold text-white mb-6">
          Security and Background Information
        </h2>

        <div className="bg-yellow-900/30 border border-yellow-600 rounded-xl p-4 mb-6">
          <p className="text-yellow-200 text-sm">
            Please answer all questions honestly. False statements may result in
            visa denial.
          </p>
        </div>

        {securityQuestions.map((q, index) => (
          <div
            key={q.name}
            className="pb-4 border-b border-gray-700 last:border-0"
          >
            <p className="text-gray-300 mb-3 text-sm">
              {index + 1}. {q.question}
            </p>
            <div className="flex gap-6">
              {["Yes", "No"].map((option) => (
                <label
                  key={option}
                  className="flex items-center space-x-2 cursor-pointer"
                >
                  <input
                    type="radio"
                    name={q.name}
                    value={option}
                    checked={formData[q.name as keyof FormData] === option}
                    onChange={() => handleRadioChange(q.name, option)}
                    className="w-4 h-4 text-blue-500 bg-gray-800 border-gray-600 focus:ring-blue-500"
                  />
                  <span className="text-gray-300">{option}</span>
                </label>
              ))}
            </div>
            {fieldErrors[q.name] && (
              <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {fieldErrors[q.name]}
              </p>
            )}
          </div>
        ))}
      </div>
    );
  };

  // Step 9: Documents
  const renderStep9 = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-white mb-6">Document Uploads</h2>

      <div className="bg-blue-900/30 border border-blue-500/30 rounded-xl p-4 mb-6">
        <p className="text-blue-300 text-sm">
          Please upload clear, high-quality images of the required documents.
        </p>
      </div>

      <div className="space-y-6">
        {renderFileUpload(
          "passportCopy",
          passportCopy,
          passportCopyRef,
          "Upload Passport Bio Page (PDF or JPG)",
          "Upload a clear scan or photo of your passport bio page. Max 1 MB.",
          "image/jpeg,image/jpg,image/png,image/webp,application/pdf",
        )}

        <div className="mt-6">
          {renderFileUpload(
            "visaPhoto",
            visaPhoto,
            visaPhotoRef,
            "Upload Photo for Visa (2x2 inch, white background)",
            "Photo must follow U.S. visa photo requirements. Should be taken no longer than 6 months ago. Max 1 MB.",
            "image/jpeg,image/jpg,image/png,image/webp",
          )}
          <p className="text-xs text-blue-400 mt-2">
            <a
              href="https://travel.state.gov/content/travel/en/us-visas/visa-information-resources/photos.html"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline"
            >
              View U.S. visa photo requirements →
            </a>
          </p>
        </div>
      </div>
    </div>
  );

  // Step 10: Review & Submit
  const renderStep10 = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-white mb-6">Review & Submit</h2>

      <div className="bg-blue-900/30 border border-blue-600 rounded-xl p-6 mb-6">
        <h3 className="text-lg font-semibold text-blue-300 mb-4">
          Application Summary
        </h3>
        <div className="grid md:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-gray-400">Full Name:</p>
            <p className="text-white">
              {formData.firstName} {formData.lastName}
            </p>
          </div>
          <div>
            <p className="text-gray-400">Date of Birth:</p>
            <p className="text-white">{formData.dateOfBirth}</p>
          </div>
          <div>
            <p className="text-gray-400">Nationality:</p>
            <p className="text-white">{formData.nationality}</p>
          </div>
          <div>
            <p className="text-gray-400">Passport Number:</p>
            <p className="text-white">{formData.passportNumber}</p>
          </div>
          <div>
            <p className="text-gray-400">Purpose of Trip:</p>
            <p className="text-white">{formData.purposeOfTrip}</p>
          </div>
          <div>
            <p className="text-gray-400">Intended Arrival:</p>
            <p className="text-white">{formData.intendedArrivalDate}</p>
          </div>
          <div>
            <p className="text-gray-400">Email:</p>
            <p className="text-white">{formData.email}</p>
          </div>
          <div>
            <p className="text-gray-400">Phone:</p>
            <p className="text-white">{formData.primaryPhone}</p>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-blue-600/50">
          <p className="text-gray-400">Documents Selected:</p>
          <div className="flex gap-4 mt-2">
            {passportCopy?.file && (
              <span className="flex items-center gap-1 text-green-400 text-sm">
                <CheckCircle className="w-4 h-4" /> Passport Bio Page
              </span>
            )}
            {visaPhoto?.file && (
              <span className="flex items-center gap-1 text-green-400 text-sm">
                <CheckCircle className="w-4 h-4" /> Visa Photo
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.consentAcknowledgment}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                consentAcknowledgment: e.target.checked,
              }))
            }
            className="w-5 h-5 mt-0.5 text-blue-500 bg-gray-800 border-gray-600 rounded focus:ring-blue-500"
          />
          <span className="text-gray-300 text-sm">
            <strong>Consent Acknowledgment (Required):</strong> I confirm that
            the information provided is accurate, and I consent to the use of
            this information for the purpose of U.S. visa processing.
          </span>
        </label>
        {fieldErrors.consentAcknowledgment && (
          <p className="text-red-400 text-sm mt-2 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {fieldErrors.consentAcknowledgment}
          </p>
        )}
      </div>

      {error && (
        <div className="bg-red-900/30 border border-red-600 rounded-xl p-4">
          <p className="text-red-400 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            {error}
          </p>
        </div>
      )}
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      case 4:
        return renderStep4();
      case 5:
        return renderStep5();
      case 6:
        return renderStep6();
      case 7:
        return renderStep7();
      case 8:
        return renderStep8();
      case 9:
        return renderStep9();
      case 10:
        return renderStep10();
      default:
        return renderStep1();
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900">
        <div className="pt-24 pb-16 px-4">
          <div className="max-w-2xl mx-auto text-center">
            <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-2xl p-12">
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-400" />
              </div>
              <h1 className="text-3xl font-bold text-white mb-4">
                Application Submitted Successfully!
              </h1>
              <p className="text-gray-300 mb-8">
                Thank you for submitting your DS-160 visa application. We will
                review your application and contact you with next steps.
              </p>
              <a
                href="/"
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-xl transition-colors"
              >
                Return to Home
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900">
      <div className="pt-24 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Title */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-white mb-2">
              DS-160 Visa Application Form
            </h1>
            <p className="text-gray-400">
              Complete all required fields to submit your visa application
            </p>
          </div>

          {/* Step Indicator */}
          {renderStepIndicator()}

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-2xl p-6 md:p-8">
              {renderCurrentStep()}

              {/* Navigation Buttons */}
              <div className="flex justify-between mt-8 pt-6 border-t border-gray-700">
                <button
                  type="button"
                  onClick={handlePrevious}
                  disabled={currentStep === 1}
                  className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all ${
                    currentStep === 1
                      ? "bg-gray-700 text-gray-500 cursor-not-allowed"
                      : "bg-gray-700 text-white hover:bg-gray-600"
                  }`}
                >
                  <ChevronLeft className="w-5 h-5" />
                  Previous
                </button>

                {currentStep === totalSteps ? (
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex items-center gap-2 px-8 py-3 bg-green-500 hover:bg-green-600 text-white font-medium rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-5 h-5" />
                        Submit Application
                      </>
                    )}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleNext}
                    className="flex items-center gap-2 px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-xl transition-all"
                  >
                    Continue
                    <ChevronRight className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default VisaApplication;
